import { useEffect, useState } from 'react';
import { api, type Analyse, type Posten, type Veranstaltung } from '../api/client';
import { Schritte } from '../components/Schritte';
import { Karte, Meldung, StatusPill, fehlerText } from '../components/ui';
import { link } from '../router';

const AUSGABEN = [
  ['lagekarte', 'Lagekarte (A3 quer)', 'Karte mit Route, allen Stellen und Legende'],
  ['postenliste', 'Postenliste (A4)', 'Alle Posten mit Zeiten, Einheit, Auftrag und Material'],
  ['postenkarten', 'Postenkarten (A4, je Posten eine Seite)', 'Zum Ausdrucken und Mitgeben'],
  [
    'einsatzbefehl',
    'Einsatzbefehl (Gerüst)',
    'Lage, Auftrag, Durchführung, Führung und Kommunikation',
  ],
] as const;

/** Schritt 5: Einsatzunterlagen ausgeben, Plan zum Lesen teilen, Postenkarten für Mobilgeräte. */
export function Ausgabe({ id, darf }: { id: string; darf: boolean }) {
  const [v, setV] = useState<Veranstaltung | null>(null);
  const [analyse, setAnalyse] = useState<Analyse | null>(null);
  const [posten, setPosten] = useState<Posten[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { veranstaltung } = await api.veranstaltung(id);
        setV(veranstaltung);
        const pv = veranstaltung.planversion_id;
        if (!pv) return;
        const [a, p, t] = await Promise.all([
          api.analyse(pv),
          api.posten(pv),
          api.teilenStatus(pv),
        ]);
        setAnalyse(a.analyse);
        setPosten(p.posten);
        setToken(t.token);
      } catch (e) {
        setFehler(fehlerText(e));
      }
    })();
  }, [id]);

  const pv = v?.planversion_id;
  const basis = window.location.href.split('#')[0];
  const teilenUrl = token ? `${basis}#/geteilt/${token}/uebersicht` : null;
  const stellenName = (sid: string) =>
    analyse?.stellen.find((s) => s.id === sid)?.bezeichnung ?? 'Stelle';

  return (
    <div className="seite">
      <Schritte id={id} aktiv="ausgabe" titel={v?.titel} />
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
      {analyse && !analyse.vollstaendig && (
        <Meldung art="info">
          Der Plan hat noch <StatusPill status="rot" text={`${analyse.zaehler.rot} rote`} /> und{' '}
          <StatusPill status="gelb" text={`${analyse.zaehler.gelb_offen} offene gelbe`} /> Befunde.
          Unterlagen lassen sich trotzdem drucken; offene Punkte stehen darauf.
        </Meldung>
      )}
      {!analyse && (
        <Meldung art="info">Noch kein Abgleich – bitte zuerst Route zeichnen und prüfen.</Meldung>
      )}

      <div className="seite--zweispaltig">
        <Karte titel="Einsatzunterlagen">
          <ul className="ausgaben">
            {pv &&
              AUSGABEN.map(([art, titel, text]) => (
                <li key={art}>
                  <a
                    className="knopf knopf--primaer"
                    href={link('druck', pv, art)}
                    target="_blank"
                    rel="noopener"
                  >
                    {titel}
                  </a>
                  <span className="gedaempft">{text}</span>
                </li>
              ))}
          </ul>
          <p className="gedaempft">
            Die Ansicht öffnet sich in einem neuen Tab. Dort „Als PDF drucken“ wählen. Alle Blätter
            sind auch in Graustufen lesbar und tragen den Hinweis „Planungshilfe“ sowie die
            OpenStreetMap-Attribution. Sie kommen vollständig aus der Datenbank – ohne Abfrage beim
            Kartendienst.
          </p>
        </Karte>

        <div>
          <Karte titel="Teilen per Link (nur lesen)">
            {teilenUrl ? (
              <>
                <p>Jede Person mit diesem Link kann den Plan ohne Anmeldung lesen:</p>
                <input
                  className="link-feld"
                  readOnly
                  value={teilenUrl}
                  onFocus={(e) => e.target.select()}
                />
                {darf && pv && (
                  <button
                    type="button"
                    className="knopf knopf--gefahr"
                    onClick={() => void api.teilenBeenden(pv).then(() => setToken(null))}
                  >
                    Link ungültig machen
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="gedaempft">Der Plan ist nicht geteilt.</p>
                {darf && pv && (
                  <button
                    type="button"
                    className="knopf"
                    onClick={() =>
                      void api
                        .teilen(pv)
                        .then((r) => setToken(r.token))
                        .catch((e: unknown) => setFehler(fehlerText(e)))
                    }
                  >
                    Lese-Link erzeugen
                  </button>
                )}
              </>
            )}
          </Karte>

          <Karte titel={`Postenkarten fürs Handy (${posten.length})`}>
            <p className="gedaempft">
              Einmal mit Netz öffnen – danach ist die Karte auf dem Gerät auch offline lesbar.
            </p>
            <ul className="einfache-liste">
              {pv &&
                posten.map((p) => (
                  <li key={p.id}>
                    <a href={link('postenkarte', pv, p.id)} target="_blank" rel="noopener">
                      {stellenName(p.sperrstelle_id)}
                    </a>{' '}
                    – {p.personen} {p.personen === 1 ? 'Person' : 'Personen'}
                  </li>
                ))}
            </ul>
          </Karte>
        </div>
      </div>
    </div>
  );
}
